/* Chef Akila – product page behaviour (ca-* sections) */
(() => {
  if (window.__caPdp) return;
  window.__caPdp = true;

  const formatMoney = (cents, format) => {
    const fmt = format || (window.Shopify && Shopify.money_format) || '£{{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, dec] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole}.${dec}`.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${dec}`);
  };

  // Every add goes through the basket's single API (assets/ca-cart.js): it refreshes the basket + bag count and shows
  // the "Added to your basket" bar (phones) / card (computers). Plain /cart/add.js is only a fallback.
  const addItems = async (items, source) => {
    if (window.CACart && typeof window.CACart.add === 'function') return window.CACart.add(items, { source });
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Sorry, this could not be added to your basket.');
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
    if (cart) document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
    return data;
  };

  /* ---------- Main product ---------- */
  class CaProduct {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-ca-form]');
      this.variants = JSON.parse(root.querySelector('[data-ca-variants]')?.textContent || '[]');
      this.moneyFormat = root.dataset.moneyFormat;
      this.idInput = root.querySelector('[data-ca-variant-input]');
      this.qtyInput = root.querySelector('[data-ca-qty-input]');
      this.atc = root.querySelector('[data-ca-atc]');
      this.error = root.querySelector('[data-ca-error]');
      this.atcText = root.querySelector('[data-ca-atc-text]');
      this.atcLabel = this.atcText?.dataset.label || 'Add to basket';
      this.soldOutLabel = this.atcText?.dataset.soldOut || 'Sold out';
      this.current = this.variants.find((v) => String(v.id) === this.idInput?.value) || this.variants[0];
      this.initGallery();
      this.initVariants();
      this.initQty();
      this.initSides();
      this.initSubmit();
      this.initSticky();
    }

    initGallery() {
      const track = this.root.querySelector('[data-ca-track]');
      if (!track) return;
      this.track = track;
      const slides = [...track.children];
      const thumbs = [...this.root.querySelectorAll('[data-ca-thumb]')];
      const go = (i, smooth = true) => {
        const n = (i + slides.length) % slides.length;
        track.scrollTo({ left: slides[n].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      };
      this.goToSlide = go;
      const current = () => Math.round(track.scrollLeft / track.clientWidth);
      this.root.querySelector('[data-ca-prev]')?.addEventListener('click', () => go(current() - 1));
      this.root.querySelector('[data-ca-next]')?.addEventListener('click', () => go(current() + 1));
      thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.caThumb))));
      let raf;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = current();
          thumbs.forEach((t, k) => t.classList.toggle('is-active', k === i));
          const active = thumbs[i];
          if (active) active.parentElement.scrollTo({ left: active.offsetLeft - 20, behavior: 'smooth' });
          slides.forEach((s, k) => { if (k !== i) s.querySelector('video')?.pause(); });
        });
      }, { passive: true });
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') go(current() + 1);
        if (e.key === 'ArrowLeft') go(current() - 1);
      });
    }

    initVariants() {
      this.root.querySelectorAll('[data-ca-variant-radio]').forEach((radio) =>
        radio.addEventListener('change', () => {
          const v = this.variants.find((x) => String(x.id) === radio.value);
          if (v) this.setVariant(v);
        })
      );
    }

    setVariant(v) {
      this.current = v;
      this.idInput.value = v.id;
      const money = (c) => formatMoney(c, this.moneyFormat);
      this.root.querySelectorAll('[data-ca-price]').forEach((el) => (el.textContent = money(v.price)));
      const portion = this.root.querySelector('[data-ca-portion]');
      if (portion) {
        portion.hidden = !(v.servesNum > 1);
        portion.textContent = `${money(v.perPortion)} a portion`;
      }
      const servesText = [v.serves ? `Serves ${v.serves}` : '', v.weight].filter(Boolean).join(' · ');
      this.root.querySelectorAll('[data-ca-serves]').forEach((el) => (el.textContent = servesText));
      const stickyServes = this.root.querySelector('[data-ca-sticky-serves]');
      if (stickyServes) stickyServes.textContent = servesText;
      if (this.atc) this.atc.disabled = !v.available;
      if (this.atcText) {
        // rebuild the label so switching from a sold-out option back to an available one restores the price
        if (v.available) {
          this.atcText.textContent = this.atcLabel;
          const wrap = document.createElement('span');
          wrap.className = 'ca-pdp__atc-price';
          wrap.append(' · ');
          const price = document.createElement('span');
          price.dataset.caAtcPrice = '';
          wrap.append(price);
          this.atcText.append(wrap);
        } else {
          this.atcText.textContent = this.soldOutLabel;
        }
      }
      const stickyBtn = this.root.querySelector('[data-ca-sticky-atc]');
      if (stickyBtn) stickyBtn.disabled = !v.available;
      this.updateTotal();
      if (v.mediaId && this.track) {
        const slide = this.track.querySelector(`[data-media-id="${v.mediaId}"]`);
        if (slide) this.goToSlide(Number(slide.dataset.index));
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
    }

    initQty() {
      if (!this.qtyInput) return;
      this.root.querySelectorAll('[data-ca-qty]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const next = Math.max(1, Math.min(99, (parseInt(this.qtyInput.value, 10) || 1) + Number(btn.dataset.caQty)));
          this.qtyInput.value = next;
          this.updateTotal();
        })
      );
      this.qtyInput.addEventListener('change', () => {
        this.qtyInput.value = Math.max(1, Math.min(99, parseInt(this.qtyInput.value, 10) || 1));
        this.updateTotal();
      });
    }

    initSides() {
      this.sides = [...this.root.querySelectorAll('[data-ca-side]')];
      this.sides.forEach((s) => s.addEventListener('change', () => this.updateTotal()));
    }

    updateTotal() {
      const qty = parseInt(this.qtyInput?.value, 10) || 1;
      const sides = (this.sides || []).filter((s) => s.checked).reduce((sum, s) => sum + Number(s.dataset.price || 0), 0);
      const total = this.current.price * qty + sides;
      const el = this.root.querySelector('[data-ca-atc-price]');
      if (el) el.textContent = formatMoney(total, this.moneyFormat);
    }

    // eGift voucher: recipient name, email and delivery date are required → Shopify emails the card on that date
    initGift() {
      this.gift = this.root.querySelector('[data-ca-gift]');
      if (!this.gift) return;
      const pad = (n) => String(n).padStart(2, '0');
      const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const now = new Date();
      const date = this.gift.querySelector('[data-ca-gift-date]');
      if (date) {
        date.min = ymd(now);
        date.max = ymd(new Date(now.getTime() + 90 * 864e5));
        if (!date.value) date.value = ymd(now);
      }
      const off = this.gift.querySelector('[data-ca-gift-offset]');
      if (off) off.value = String(now.getTimezoneOffset());
      this.gift.addEventListener('input', (e) => {
        const f = e.target.closest('.ca-gift__field');
        if (f) { f.classList.remove('is-invalid'); e.target.removeAttribute('aria-invalid'); }
      });
    }

    giftProperties() {
      if (!this.gift) return null;
      const props = {};
      let bad = null;
      this.gift.querySelectorAll('[data-ca-gift-prop]').forEach((el) => {
        const v = (el.value || '').trim();
        let ok = true;
        if (el.required) {
          if (!v) ok = false;
          else if (el.type === 'email') ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
          else if (el.type === 'date') ok = /^\d{4}-\d{2}-\d{2}$/.test(v) && (!el.min || v >= el.min) && (!el.max || v <= el.max);
        }
        const f = el.closest('.ca-gift__field');
        if (f) f.classList.toggle('is-invalid', !ok);
        if (!ok) { el.setAttribute('aria-invalid', 'true'); if (!bad) bad = el; } else el.removeAttribute('aria-invalid');
        if (v) props[el.dataset.caGiftProp] = v;
      });
      if (bad) {
        if (this.error) { this.error.textContent = bad.dataset.caGiftMsg || 'Please fill in the recipient details.'; this.error.hidden = false; }
        bad.scrollIntoView({ block: 'center', behavior: 'smooth' });
        bad.focus({ preventScroll: true });
        return false;
      }
      return props;
    }

    initSubmit() {
      this.initGift();
      if (!this.form) return;
      this.form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!this.current?.available || this.atc?.getAttribute('aria-busy') === 'true') return;
        const qty = parseInt(this.qtyInput?.value, 10) || 1;
        const gift = this.giftProperties();
        if (gift === false) return;
        const items = [gift ? { id: this.current.id, quantity: qty, properties: gift } : { id: this.current.id, quantity: qty }];
        (this.sides || []).filter((s) => s.checked && !s.disabled).forEach((s) => items.push({ id: Number(s.value), quantity: 1 }));
        this.atc?.setAttribute('aria-busy', 'true');
        if (this.error) this.error.hidden = true;
        try {
          await addItems(items, 'pdp');
          (this.sides || []).forEach((s) => (s.checked = false));
          if (this.gift) this.gift.querySelectorAll('input[type=text], input[type=email], textarea').forEach((el) => (el.value = ''));
          if (this.qtyInput) this.qtyInput.value = 1;
          this.updateTotal();
        } catch (err) {
          if (this.error) {
            this.error.textContent = err.message;
            this.error.hidden = false;
          }
        } finally {
          this.atc?.removeAttribute('aria-busy');
        }
      });
    }

    initSticky() {
      const bar = this.root.querySelector('[data-ca-sticky]');
      const row = this.root.querySelector('[data-ca-buy-row]');
      if (!bar || !row || !('IntersectionObserver' in window)) return;
      const btn = bar.querySelector('[data-ca-sticky-atc]');
      btn.addEventListener('click', () => this.form.requestSubmit ? this.form.requestSubmit() : this.form.dispatchEvent(new Event('submit', { cancelable: true })));
      new IntersectionObserver(([entry]) => {
        // show once the main button has scrolled up out of view
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        btn.tabIndex = show ? 0 : -1;
      }).observe(row);
    }
  }

  /* ---------- Reviews: top up with more of this dish's Judge.me reviews ---------- */
  const esc = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const reviewCard = (r, label) => `
    <figure class="ca-review">
      <figcaption class="ca-review__who">
        <span class="ca-review__avatar" aria-hidden="true">${esc(r.initials)}</span>
        <span class="ca-review__meta">
          <span class="ca-review__name">${esc(r.name)}</span>
          <span class="ca-review__product">${esc(label)}</span>
          <span class="ca-visually-hidden">${r.score} out of 5 stars</span>
          <span class="ca-stars" aria-hidden="true">${STAR.repeat(5)}</span>
        </span>
      </figcaption>
      <blockquote class="ca-review__quote">“${esc(r.body)}”</blockquote>
    </figure>`;
  const STAR = '<svg class="ca-icon" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8.243 7.34l-6.38 .925l-.113 .023a1 1 0 0 0 -.44 1.684l4.622 4.499l-1.09 6.355l-.013 .11a1 1 0 0 0 1.464 .944l5.706 -3l5.693 3l.1 .046a1 1 0 0 0 1.352 -1.1l-1.091 -6.355l4.624 -4.5l.078 -.085a1 1 0 0 0 -.633 -1.62l-6.38 -.926l-2.852 -5.78a1 1 0 0 0 -1.794 0l-2.853 5.78z"/></svg>';

  const loadReviews = async (track) => {
    const d = track.dataset;
    if (!d.jdgmShop || !d.jdgmProduct) return false;
    const limit = Number(d.limit) || 8;
    if (track.querySelectorAll('.ca-review').length >= limit) return false;
    const base = `https://judge.me/reviews/%s?url=${d.jdgmShop}&shop_domain=${d.jdgmShop}&platform=shopify&page=1&per_page=40`;
    const parse = async (url, own) => {
      const json = await fetch(url).then((r) => (r.ok ? r.json() : null)).catch(() => null);
      if (!json || !json.html) return [];
      const doc = new DOMParser().parseFromString(json.html, 'text/html');
      return [...doc.querySelectorAll('.jdgm-rev')]
        .map((el) => {
          const name = (el.querySelector('.jdgm-rev__author')?.textContent || '').trim();
          const parts = name.split(/\s+/).filter(Boolean);
          const first = parts[0] ? parts[0][0].toUpperCase() + parts[0].slice(1) : '';
          const last = parts.length > 1 ? parts[parts.length - 1][0].toUpperCase() : '';
          return {
            id: el.dataset.reviewId,
            score: Number(el.querySelector('[data-score]')?.dataset.score || 0),
            body: (el.querySelector('.jdgm-rev__body')?.textContent || '').trim().replace(/\s+/g, ' '),
            name: last ? `${first} ${last}.` : first,
            initials: (parts[0]?.[0] || '').toUpperCase() + last,
            // reviews of other dishes are labelled with that dish
            label: own ? d.label : (el.querySelector('.jdgm-rev__prod-link')?.textContent || '').trim(),
            photo: own ? '' : el.querySelector('.jdgm-rev__product-picture')?.getAttribute('href') || '',
          };
        })
        .filter((r) => r.name && r.name !== 'Anonymous' && r.label && r.score >= (Number(d.minScore) || 5) && r.body.length >= (Number(d.minChars) || 0));
    };
    let reviews = (await parse(`${base.replace('%s', 'reviews_for_widget')}&product_id=${d.jdgmProduct}`, true)).slice(0, limit);
    // Not enough reviews of this dish yet (e.g. a new curry box) → fill up with the latest reviews from across the shop
    if (reviews.length < limit && d.shopFill !== undefined) {
      const seen = new Set(reviews.map((r) => r.id));
      const shop = (await parse(`${base.replace('%s', 'all_reviews_js_based')}&review_type=product-reviews`, false)).filter((r) => !seen.has(r.id));
      reviews = reviews.concat(shop).slice(0, limit);
    }
    if (reviews.length <= track.querySelectorAll('.ca-review').length) return false;
    const photos = d.showPhotos !== undefined ? JSON.parse(d.photos || '[]') : [];
    track.innerHTML = reviews
      .map((r, i) => {
        // a review of another dish sits next to a photo of that dish
        const src = r.photo && /^https:\/\/cdn\.shopify\.com\//.test(r.photo) ? `${r.photo}${r.photo.includes('?') ? '&' : '?'}width=600` : photos[i % photos.length];
        return reviewCard(r, r.label) + (photos.length ? `<div class="ca-review-photo"><img src="${esc(src)}" alt="" loading="lazy" width="600" height="600"></div>` : '');
      })
      .join('');
    return true;
  };

  /* ---------- Reviews carousel ---------- */
  const initCarousel = async (section) => {
    const track = section.querySelector('[data-ca-carousel]');
    if (!track || track.dataset.ready) return;
    track.dataset.ready = '1';
    await loadReviews(track);
    const dotsWrap = section.querySelector('[data-ca-carousel-dots]');
    const items = () => [...track.children];
    const step = () => {
      const first = items()[0];
      if (!first) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    };
    const perView = () => Math.max(1, Math.floor((track.clientWidth + 1) / step()));
    const pages = () => Math.max(1, Math.ceil((track.scrollWidth - track.clientWidth) / (step() * perView())) + 1);
    const page = () => Math.round(track.scrollLeft / (step() * perView()));
    const go = (p) => {
      const max = track.scrollWidth - track.clientWidth;
      track.scrollTo({ left: Math.max(0, Math.min(max, p * step() * perView())), behavior: 'smooth' });
    };
    const buildDots = () => {
      if (!dotsWrap) return;
      const n = Math.min(pages(), 8);
      dotsWrap.innerHTML = '';
      for (let i = 0; i < n; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ca-reviews__dot';
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.addEventListener('click', () => go(i));
        dotsWrap.appendChild(b);
      }
      sync();
    };
    const sync = () => {
      const p = Math.min(page(), (dotsWrap?.children.length || 1) - 1);
      dotsWrap?.querySelectorAll('.ca-reviews__dot').forEach((d, i) => d.classList.toggle('is-active', i === p));
    };
    // Both arrows wrap round, so the reviews never come to a stop
    section.querySelector('[data-ca-carousel-prev]')?.addEventListener('click', () => go(track.scrollLeft <= 4 ? pages() : page() - 1));
    section.querySelector('[data-ca-carousel-next]')?.addEventListener('click', () => {
      const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
      go(atEnd ? 0 : page() + 1);
    });
    track.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
    // Mouse drag (touch and trackpads already scroll natively)
    let drag = null;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, left: track.scrollLeft, moved: 0 };
      track.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', (e) => {
      if (!drag || drag.x === undefined) return;
      drag.moved = e.clientX - drag.x;
      track.scrollLeft = drag.left - drag.moved;
    });
    window.addEventListener('pointerup', () => {
      if (!drag || drag.x === undefined) return;
      const { moved, left } = drag;
      drag = moved ? { moved } : null;
      track.classList.remove('is-dragging');
      const s = step();
      const i = Math.round(left / s) + (Math.abs(moved) > 40 ? (moved < 0 ? 1 : -1) * Math.max(1, Math.round(Math.abs(moved) / s)) : 0);
      track.scrollTo({ left: Math.max(0, Math.min(track.scrollWidth - track.clientWidth, i * s)), behavior: 'smooth' });
      setTimeout(() => { drag = null; });
    });
    track.addEventListener('click', (e) => { if (drag && Math.abs(drag.moved) > 5) { e.preventDefault(); e.stopPropagation(); } }, true);
    track.addEventListener('dragstart', (e) => e.preventDefault());
    window.addEventListener('resize', buildDots);
    buildDots();
  };

  /* ---------- Video ---------- */
  const initVideo = (btn) => {
    if (btn.dataset.ready) return;
    btn.dataset.ready = '1';
    btn.addEventListener('click', () => {
      if (btn.classList.contains('is-playing')) return;
      let el;
      if (btn.dataset.youtube) {
        el = document.createElement('iframe');
        el.src = `https://www.youtube-nocookie.com/embed/${btn.dataset.youtube}?autoplay=1&rel=0&playsinline=1`;
        el.allow = 'autoplay; encrypted-media; picture-in-picture';
        el.allowFullscreen = true;
        el.title = btn.getAttribute('aria-label') || 'Video';
      } else if (btn.dataset.vimeo) {
        el = document.createElement('iframe');
        el.src = `https://player.vimeo.com/video/${btn.dataset.vimeo}?autoplay=1`;
        el.allow = 'autoplay; fullscreen; picture-in-picture';
        el.title = btn.getAttribute('aria-label') || 'Video';
      } else {
        const tpl = btn.querySelector('template');
        if (!tpl) return;
        el = tpl.content.firstElementChild.cloneNode(true);
      }
      btn.appendChild(el);
      btn.classList.add('is-playing');
      if (el.tagName === 'VIDEO') el.play?.().catch(() => {});
    });
  };

  /* ---------- Recommendations + quick add ---------- */
  const initAlso = async (section) => {
    const url = section.dataset.url;
    if (!url || section.dataset.loaded) return;
    section.dataset.loaded = '1';
    try {
      const html = await fetch(url).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const grid = doc.querySelector('[data-ca-also-grid]');
      if (grid && grid.children.length) section.querySelector('[data-ca-also-grid]').replaceWith(grid);
      else section.hidden = true;
    } catch (e) {
      /* leave the section as rendered */
    }
  };

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-ca-quick-add]');
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    try {
      await addItems([{ id: Number(btn.dataset.caQuickAdd), quantity: 1 }], 'also-like');
    } catch (err) {
      // show the reason on the button itself for a few seconds (a tooltip alone is invisible to most shoppers)
      if (!btn.dataset.caHtml) btn.dataset.caHtml = btn.innerHTML;
      btn.setAttribute('aria-live', 'polite');
      btn.textContent = err.message || 'Could not add. Please try again.';
      clearTimeout(btn.__caT);
      btn.__caT = setTimeout(() => { btn.innerHTML = btn.dataset.caHtml; delete btn.dataset.caHtml; }, 3500);
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });

  const init = (scope = document) => {
    scope.querySelectorAll('[data-ca-pdp]').forEach((root) => {
      if (root.__caProduct) return;
      root.__caProduct = new CaProduct(root);
    });
    scope.querySelectorAll('.ca-reviews').forEach(initCarousel);
    scope.querySelectorAll('[data-ca-video]').forEach(initVideo);
    scope.querySelectorAll('[data-ca-also][data-url]').forEach(initAlso);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
